import React, { useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import {
  CarrierConfig,
  CreateCarrierConfigRequest,
  UpdateCarrierConfigRequest,
} from "../../model/SiteShipping";
import { X } from "lucide-react";

interface Props {
  carrier: CarrierConfig | null;
  onClose: (saved: boolean) => void;
}

const CARRIER_SLUGS = [
  "ctt",
  "ctt_express",
  "dhl",
  "ups",
  "fedex",
  "gls",
  "dpd",
  "seur",
  "manual",
];

const ShippingCarrierForm: React.FC<Props> = ({ carrier, onClose }) => {
  const isEdit = !!carrier;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [carrierSlug, setCarrierSlug] = useState(carrier?.carrierSlug || "");
  const [displayName, setDisplayName] = useState(carrier?.displayName || "");
  const [testMode, setTestMode] = useState(carrier?.testMode ?? false);
  const [isDefault, setIsDefault] = useState(carrier?.isDefault ?? false);
  const [weightUnit, setWeightUnit] = useState(carrier?.weightUnit || "kg");
  const [dimensionUnit, setDimensionUnit] = useState(
    carrier?.dimensionUnit || "cm",
  );
  const [insuranceEnabled, setInsuranceEnabled] = useState(
    carrier?.insuranceEnabled ?? false,
  );
  const [signatureRequired, setSignatureRequired] = useState(
    carrier?.signatureRequired ?? false,
  );
  const [accountNumber, setAccountNumber] = useState(
    carrier?.accountNumber || "",
  );
  const [credentialsJson, setCredentialsJson] = useState("{}");

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      let creds: Record<string, string> = {};
      try {
        creds = JSON.parse(credentialsJson);
      } catch {
        setError("Credenciais invalidas (JSON).");
        setSaving(false);
        return;
      }

      if (isEdit) {
        const body: UpdateCarrierConfigRequest = {
          displayName,
          testMode,
          isDefault,
          weightUnit,
          dimensionUnit,
          insuranceEnabled,
          signatureRequired,
          accountNumber,
        };
        if (credentialsJson !== "{}") body.credentials = creds;
        await api.patch(`${API_URLS.SHIPPING_CARRIERS}/${carrier!.id}`, body);
      } else {
        const body: CreateCarrierConfigRequest = {
          carrierSlug,
          displayName,
          credentials: creds,
          testMode,
          isDefault,
          weightUnit,
          dimensionUnit,
          insuranceEnabled,
          signatureRequired,
          accountNumber,
        };
        await api.post(API_URLS.SHIPPING_CARRIERS, body);
      }
      onClose(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">
            {isEdit ? "Editar Transportadora" : "Nova Transportadora"}
          </h3>
          <button onClick={() => onClose(false)}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Carrier Slug
              </label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={carrierSlug}
                onChange={(e) => setCarrierSlug(e.target.value)}
              >
                <option value="">Selecionar...</option>
                {CARRIER_SLUGS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome de Exibicao
            </label>
            <input
              type="text"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numero de Conta
            </label>
            <input
              type="text"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unidade de Peso
              </label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={weightUnit}
                onChange={(e) => setWeightUnit(e.target.value)}
              >
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="lb">lb</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unidade de Dimensao
              </label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={dimensionUnit}
                onChange={(e) => setDimensionUnit(e.target.value)}
              >
                <option value="cm">cm</option>
                <option value="in">in</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={testMode}
                onChange={(e) => setTestMode(e.target.checked)}
                className="w-4 h-4"
              />
              Modo Teste
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="w-4 h-4"
              />
              Default
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={insuranceEnabled}
                onChange={(e) => setInsuranceEnabled(e.target.checked)}
                className="w-4 h-4"
              />
              Seguro
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={signatureRequired}
                onChange={(e) => setSignatureRequired(e.target.checked)}
                className="w-4 h-4"
              />
              Assinatura
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Credenciais (JSON)
            </label>
            <textarea
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
              rows={3}
              value={credentialsJson}
              onChange={(e) => setCredentialsJson(e.target.value)}
              placeholder='{"api_key": "...", "secret": "..."}'
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <button
            className="button is-light is-small"
            onClick={() => onClose(false)}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="button is-primary is-small"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShippingCarrierForm;
